import React, { useState, useEffect, useRef } from 'react';
import './ReportingMonthInput.css';
import { closedThroughFor } from '../../services/booksClosedService';

// "2026-10" as a person reads it: "October 2026".
const monthInWords = (yyyyMm) => {
  const [year, month] = yyyyMm.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleString('en-GB', { month: 'long', year: 'numeric' });
};

// The month after "2026-10": "2026-11".
const nextMonthValue = (yyyyMm) => {
  const [year, month] = yyyyMm.split('-').map(Number);
  return month === 12 ? `${year + 1}-01` : `${year}-${String(month + 1).padStart(2, '0')}`;
};

/**
 * Custom Reporting Month Input Component
 * Displays YYYY-MM format with placeholder masking
 * Shows red border on invalid format
 * Auto-initializes with current month
 *
 * ``entityId`` (the invoice screens): finance closes each company's months
 * ("Books closed up to", Chris 2026-10-17). A closed month turns the box red
 * with the reason in words, also when an old draft opens with one. The server
 * refuses it on save either way (invoicing/services/reporting_month.py).
 */
const ReportingMonthInput = ({ id, value, onChange, onClear, className = '', disabled = false, entityId = null }) => {
  const [displayValue, setDisplayValue] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [closedThrough, setClosedThrough] = useState(null);

  useEffect(() => {
    let current = true;
    setClosedThrough(null);
    closedThroughFor(entityId).then((month) => { if (current) setClosedThrough(month); });
    return () => { current = false; };
  }, [entityId]);

  const closedMessage = !disabled && closedThrough && /^\d{4}-\d{2}$/.test(displayValue) && displayValue <= closedThrough
    ? `${monthInWords(displayValue)} is closed. Pick ${monthInWords(nextMonthValue(closedThrough))} or later.`
    : null;
  const isInitializedRef = useRef(false);

  // Convert backend format (YYYY-MM-DD) to display format (YYYY-MM)
  const convertToDisplayFormat = (val) => {
    if (!val) return '';
    // If value is in YYYY-MM-DD format, strip the day part
    if (val.length === 10 && val.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return val.substring(0, 7);
    }
    return val;
  };

  // Initialize with current month if no value provided (only once)
  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      if (!value) {
        const now = new Date();
        const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        onChange(currentMonth);
        setDisplayValue(currentMonth);
        setIsValid(true);
      } else {
        const displayVal = convertToDisplayFormat(value);
        setDisplayValue(displayVal);
        validateFormat(displayVal);
      }
    }
  }, []);

  // Update display value when value prop changes (for external updates)
  useEffect(() => {
    if (value) {
      const displayVal = convertToDisplayFormat(value);
      if (displayVal !== displayValue) {
        setDisplayValue(displayVal);
        validateFormat(displayVal);
      }
    }
  }, [value]);

  const validateFormat = (val) => {
    if (!val || val.length < 7) {
      setIsValid(false);
      return false;
    }
    
    // Check YYYY-MM format
    const regex = /^(\d{4})-(\d{2})$/;
    const match = val.match(regex);
    
    if (!match) {
      setIsValid(false);
      return false;
    }
    
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    
    // Validate year (1900-2100) and month (01-12)
    const valid = year >= 1900 && year <= 2100 && month >= 1 && month <= 12;
    setIsValid(valid);
    return valid;
  };

  const handleChange = (e) => {
    let input = e.target.value;
    
    // If user deleted the dash, remove the digit before it
    if (displayValue.length === 5 && input.length === 4 && input === displayValue.slice(0, 4)) {
      input = input.slice(0, 3);
    }
    
    // Strip all non-digits
    const digitsOnly = input.replace(/\D/g, '');
    
    // Auto-format: YYYY-MM
    let formatted = digitsOnly;
    if (digitsOnly.length >= 4) {
      formatted = digitsOnly.slice(0, 4) + '-' + digitsOnly.slice(4, 6);
    }
    
    setDisplayValue(formatted);
    
    // Validate and notify parent when we have complete input
    if (formatted.length === 7) {
      const valid = validateFormat(formatted);
      onChange(formatted);
      
      // Clear error styling if field is being corrected
      if (valid && onClear) {
        onClear();
      }
    } else {
      // For incomplete input, mark as invalid
      setIsValid(false);
    }
  };

  const handleBlur = () => {
    if (displayValue && displayValue.length === 7) {
      validateFormat(displayValue);
    } else if (displayValue && displayValue.length > 0) {
      // If partially filled on blur, mark as invalid
      setIsValid(false);
    }
  };

  const handleKeyDown = (e) => {
    const key = e.key;
    
    // Allow control keys
    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) {
      return;
    }
    
    // Allow Ctrl/Cmd combinations
    if (e.ctrlKey || e.metaKey) {
      return;
    }
    
    // Only allow digits
    if (!/^\d$/.test(key)) {
      e.preventDefault();
    }
  };

  return (
    <>
    <input
      id={id}
      type="text"
      className={`${className} reporting-month-input ${(!isValid && displayValue) || closedMessage ? 'reporting-month-invalid' : ''}`}
      aria-invalid={closedMessage ? 'true' : undefined}
      aria-describedby={closedMessage ? `${id}-closed` : undefined}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder="YYYY-MM"
      maxLength={7}
      autoComplete="off"
      readOnly={disabled}
    />
    {closedMessage && (
      <span id={`${id}-closed`} className="reporting-month-closed" role="alert">{closedMessage}</span>
    )}
    </>
  );
};

export default ReportingMonthInput;

